import { useMemo } from 'react'
import * as THREE from 'three'

interface PropsDetritosPersistentes {
  tipo: 'poLaminado' | 'estilhacosTemperado'
  local: 'vitima' | 'cobertor' | 'lagrima' | 'solo'
  posicaoBase: [number, number, number]
  escala?: number
}

/**
 * Renderiza o acúmulo persistente de detritos de vidro (estilhaços e pó de sílica).
 *
 * Mostra visualmente o resultado do corte/quebra:
 * - Se na vítima: comprova que a falta de EPI/proteção física atingiu o paciente.
 * - Se no cobertor ou na lágrima: comprova didaticamente que o equipamento reteve
 *   os projéteis, preservando a integridade física da vítima.
 */
export function DetritosVidroPersistentes3D({
  tipo,
  local,
  posicaoBase,
  escala = 1,
}: PropsDetritosPersistentes) {
  const numParticulas = local === 'solo' ? 120 : local === 'vitima' ? 85 : 70

  const { posicoes, tamanhos, rotacoes } = useMemo(() => {
    const pos = new Float32Array(numParticulas * 3)
    const sz = new Float32Array(numParticulas)
    const rot = new Float32Array(numParticulas * 3)

    for (let i = 0; i < numParticulas; i++) {
      let dx = 0
      let dy = 0
      let dz = 0

      if (local === 'vitima') {
        // Dispersão anatômica: cabeça, tórax e colo da vítima
        const regiao = Math.random()
        if (regiao < 0.3) {
          // Colo / coxas
          dx = (Math.random() - 0.5) * 0.3
          dy = 0.12 + Math.random() * 0.08
          dz = 0.15 + Math.random() * 0.25
        } else if (regiao < 0.7) {
          // Tórax / ombros
          dx = (Math.random() - 0.5) * 0.34
          dy = 0.35 + Math.random() * 0.25
          dz = -0.05 + Math.random() * 0.15
        } else {
          // Rosto / cabeça
          dx = (Math.random() - 0.5) * 0.16
          dy = 0.65 + Math.random() * 0.12
          dz = -0.02 + Math.random() * 0.1
        }
      } else if (local === 'cobertor') {
        // Sobre a lona drapeada
        dx = (Math.random() - 0.5) * 0.5
        dy = 0.3 + Math.random() * 0.3
        dz = (Math.random() - 0.5) * 0.4
      } else if (local === 'lagrima') {
        // Sobre a superfície curva do escudo Lágrima
        dx = (Math.random() - 0.5) * 0.3
        dy = (Math.random() - 0.5) * 0.4
        dz = 0.02 + Math.random() * 0.03
      } else {
        // No chão externo (placa empurrada de dentro para fora)
        dx = (Math.random() - 0.5) * 0.7
        dy = 0.01 + Math.random() * 0.02
        dz = (Math.random() - 0.5) * 0.7
      }

      pos[i * 3] = dx
      pos[i * 3 + 1] = dy
      pos[i * 3 + 2] = dz

      if (local === 'solo') {
        sz[i] = tipo === 'poLaminado' ? 0.012 + Math.random() * 0.016 : 0.032 + Math.random() * 0.038
      } else {
        sz[i] = tipo === 'poLaminado' ? 0.008 + Math.random() * 0.012 : 0.018 + Math.random() * 0.024
      }
      rot[i * 3] = Math.random() * Math.PI
      rot[i * 3 + 1] = Math.random() * Math.PI
      rot[i * 3 + 2] = Math.random() * Math.PI
    }

    return { posicoes: pos, tamanhos: sz, rotacoes: rot }
  }, [local, numParticulas, tipo])

  const cor = tipo === 'poLaminado' ? '#e2e8f0' : '#f0f9ff'
  const emissivo = tipo === 'poLaminado' ? '#475569' : '#38bdf8'

  return (
    <group position={posicaoBase} scale={escala}>
      {/* Placa de vidro craquelada no solo com brilho cristalino */}
      {local === 'solo' && (
        <group position={[0, 0.01, 0]}>
          <mesh rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[0.95, 0.75]} />
            <meshStandardMaterial
              color="#bae6fd"
              emissive="#0284c7"
              emissiveIntensity={0.25}
              roughness={0.1}
              metalness={0.4}
              transparent
              opacity={0.45}
              depthWrite={false}
            />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.002, 0]}>
            <planeGeometry args={[0.95, 0.75, 14, 10]} />
            <meshBasicMaterial
              color="#ffffff"
              wireframe
              transparent
              opacity={0.85}
            />
          </mesh>
        </group>
      )}

      {/* Instâncias ou pontos de cacos de vidro */}
      {Array.from({ length: numParticulas }).map((_, i) => (
        <mesh
          key={i}
          position={[posicoes[i * 3]!, posicoes[i * 3 + 1]!, posicoes[i * 3 + 2]!]}
          rotation={[rotacoes[i * 3]!, rotacoes[i * 3 + 1]!, rotacoes[i * 3 + 2]!]}
        >
          {tipo === 'poLaminado' ? (
            <sphereGeometry args={[tamanhos[i], 6, 6]} />
          ) : (
            <boxGeometry args={[tamanhos[i], tamanhos[i] * 0.6, tamanhos[i] * 1.2]} />
          )}
          <meshStandardMaterial
            color={cor}
            emissive={emissivo}
            emissiveIntensity={local === 'solo' ? 0.6 : 0.25}
            roughness={tipo === 'poLaminado' ? 0.9 : 0.08}
            metalness={tipo === 'poLaminado' ? 0.1 : 0.85}
            transparent
            opacity={tipo === 'poLaminado' ? 0.7 : 0.95}
          />
        </mesh>
      ))}
    </group>
  )
}

